import { useState, useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { TimeInformationDatabaseData, DatenformOption } from '../../../../../types/common';
import { timeInfoApi } from '@/api';
import {
  ComponentState,
  TimeCheckboxField,
  IndependentTimeData,
  FeiertageTimeData,
  UseTimeInformationReturn
} from '../types';
import { INITIAL_COMPONENT_STATE, INITIAL_INDEPENDENT_DATA, INITIAL_FEIERTAGE_DATA } from '../constants';
import { convertComponentStateToDatabase, convertDatabaseToComponentState } from '../../../utils/timeInfoConverter';

/**
 * Main hook for TimeInformation state management and handlers
 */
export const useTimeInformation = (
  sessionId: string | undefined,
  onChange?: (data: TimeInformationDatabaseData) => void,
  onDirtyChange?: (isDirty: boolean) => void
): UseTimeInformationReturn => {
  const { t } = useTranslation('training');

  const [state, setState] = useState<ComponentState>(INITIAL_COMPONENT_STATE);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const abortControllerRef = useRef<AbortController | null>(null);

  // Track previous isDirty to avoid unnecessary callbacks
  const prevIsDirtyRef = useRef<boolean>(false);

  // FIX-8 (API storm): Stable refs for callbacks/t so the fetch effect doesn't
  // re-fire on i18n re-init or parent re-renders. Only sessionId change must
  // trigger a re-fetch.
  const onChangeRef = useRef(onChange);
  const tRef = useRef(t);

  useEffect(() => {
    onChangeRef.current = onChange;
    tRef.current = t;
  }, [onChange, t]);

  // Notify parent when isDirty changes
  useEffect(() => {
    if (prevIsDirtyRef.current !== isDirty && onDirtyChange) {
      onDirtyChange(isDirty);
    }
    prevIsDirtyRef.current = isDirty;
  }, [isDirty, onDirtyChange]);

  // Fetch initial data with abort controller
  useEffect(() => {
    if (!sessionId) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const signal = abortControllerRef.current.signal;

    setIsLoading(true);
    setErrorMessage(null);

    const fetchTimeInfo = async () => {
      try {
        const data = await timeInfoApi.getTimeInfo(sessionId);

        if (signal.aborted) return;

        if (data && typeof data === 'object' && Object.keys(data).length > 0) {
          const convertedState = convertDatabaseToComponentState(data as TimeInformationDatabaseData);
          setState(convertedState);

          if (onChangeRef.current) {
            const databaseData = convertComponentStateToDatabase(convertedState);
            onChangeRef.current(databaseData);
          }
        } else {
          setState(INITIAL_COMPONENT_STATE);
        }
      } catch (error) {
        if (signal.aborted || (error instanceof Error && error.name === 'AbortError')) {
          return;
        }
        console.error('Error fetching time info:', error);
        setErrorMessage(tRef.current('training.notifications.errors.loadingTimeInfo'));
        setState(INITIAL_COMPONENT_STATE);
      } finally {
        if (!signal.aborted) {
          setIsLoading(false);
          setIsDirty(false); // Data freshly loaded, not dirty
        }
      }
    };

    fetchTimeInfo();

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [sessionId]);

  // Notify parent when state changes
  useEffect(() => {
    if (onChange) {
      const databaseData = convertComponentStateToDatabase(state);
      onChange(databaseData);
    }
  }, [state, onChange]);

  // Generic field updater
  const updateFieldData = useCallback((
    field: TimeCheckboxField,
    updates: Partial<IndependentTimeData & FeiertageTimeData>
  ) => {
    setState(prevState => {
      const newState = { ...prevState };
      newState[field] = {
        checked: prevState[field].checked,
        data: { ...prevState[field].data, ...updates } as IndependentTimeData & FeiertageTimeData
      };
      return newState;
    });
    setIsDirty(true); // Mark as dirty when user changes data
  }, []);

  const handleCheckboxChange = useCallback((field: TimeCheckboxField) => {
    setState(prevState => {
      const isChecked = !prevState[field].checked;

      if (field === 'feiertag') {
        return {
          ...prevState,
          feiertag: {
            checked: isChecked,
            data: isChecked ? { ...prevState.feiertag.data } : { ...INITIAL_FEIERTAGE_DATA }
          }
        };
      }

      return {
        ...prevState,
        [field]: {
          checked: isChecked,
          data: isChecked ? { ...prevState[field].data } : { ...INITIAL_INDEPENDENT_DATA }
        }
      };
    });
    setIsDirty(true);
  }, []);

  const handleTimezoneChange = useCallback((value: string) => {
    setState(prevState => ({ ...prevState, zeitzone: value }));
    setIsDirty(true);
  }, []);

  const handleDetailedCalcChange = useCallback((field: TimeCheckboxField) => {
    setState(prevState => {
      const newState = { ...prevState };
      newState[field] = {
        checked: prevState[field].checked,
        data: { ...prevState[field].data, detaillierteBerechnung: !prevState[field].data.detaillierteBerechnung } as IndependentTimeData & FeiertageTimeData
      };
      return newState;
    });
    setIsDirty(true);
  }, []);

  const handleDatenformChange = useCallback((field: TimeCheckboxField, e: React.ChangeEvent<HTMLSelectElement>) => {
    updateFieldData(field, { datenform: e.target.value as DatenformOption });
  }, [updateFieldData]);

  const handleZeithorizontChange = useCallback((
    field: TimeCheckboxField,
    fieldId: 'zeithorizontStart' | 'zeithorizontEnd',
    value: string
  ) => {
    updateFieldData(field, { [fieldId]: value });
  }, [updateFieldData]);

  const handleSkalierungChange = useCallback((field: TimeCheckboxField) => {
    setState(prevState => {
      const newSkalierung = prevState[field].data.skalierung === 'ja' ? 'nein' : 'ja';
      const newState = { ...prevState };
      newState[field] = {
        checked: prevState[field].checked,
        data: { ...prevState[field].data, skalierung: newSkalierung } as IndependentTimeData & FeiertageTimeData
      };
      return newState;
    });
    setIsDirty(true);
  }, []);

  const handleSkalierungValueChange = useCallback((
    field: TimeCheckboxField,
    fieldId: 'skalierungMin' | 'skalierungMax',
    value: string
  ) => {
    updateFieldData(field, { [fieldId]: value });
  }, [updateFieldData]);

  const handleLandChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    updateFieldData('feiertag', { land: e.target.value });
  }, [updateFieldData]);

  // Save data to backend
  const saveData = useCallback(async (): Promise<boolean> => {
    if (!sessionId) return false;

    setIsSaving(true);
    setErrorMessage(null);

    try {
      const databaseData = convertComponentStateToDatabase(state);
      // Cast to API expected type - category_data structure is compatible
      await timeInfoApi.upsertTimeInfo(sessionId, databaseData as unknown as Parameters<typeof timeInfoApi.upsertTimeInfo>[1]);
      setIsDirty(false); // Mark as clean after successful save
      return true;
    } catch (error) {
      console.error('Error saving time info:', error);
      setErrorMessage(t('training.notifications.errors.savingTimeInfo'));
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [sessionId, state, t]);

  return {
    state,
    isLoading,
    isSaving,
    isDirty,
    errorMessage,
    handleCheckboxChange,
    handleTimezoneChange,
    handleDetailedCalcChange,
    handleDatenformChange,
    handleZeithorizontChange,
    handleSkalierungChange,
    handleSkalierungValueChange,
    handleLandChange,
    saveData
  };
};
